import React from "react";
import SinglePaymentExtracredit from "./SinglePaymentExtracredit";
import SinglePaymentWallet from "./SinglePaymentWallet";
import { usePayment } from "../../Checkout/context/PaymentContext";
import localeText from "../../../../../locale";
import { RadioButton } from "primereact/radiobutton";
import { formatPrice } from "../../../../../utilities/formatPrice";
import { useExtracreditPayment } from "../../Checkout/hooks/useExtracreditPayment";
import { useWalletPayment } from "../../Checkout/hooks/useWalletPayment";

const SinglePaymentCreditCombined = ({
  extracoinInWallet,
  walletBalanceValue,
  combinePayment,
  totalPrice,
}) => {
  const {
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    setAmountExtracredit,
    setAmountWallet,
    walletExtracoinBalance,
    walletExtracoinBalanceForex,
    currencyFormat,
    currencyProduct,
  } = usePayment();
  const { clearExtracreditPayment } = useExtracreditPayment();
  const { clearWalletPayment } = useWalletPayment();

  const handlePaymentMethodChange = (paymentMethod) => {
    clearExtracreditPayment();
    clearWalletPayment();

    setSelectedPaymentMethod(paymentMethod);

    if (paymentMethod === "extracredit") {
      setAmountExtracredit(totalPrice);
    } else if (paymentMethod === "wallet") {
      setAmountWallet(totalPrice);
    }
  };

  if (!combinePayment) {
    const hasExtracredit = extracoinInWallet > 0;
    const hasEnoughExtracredit = extracoinInWallet >= totalPrice;
    const hasWalletBalance = walletBalanceValue > 0;
    const hasEnoughWalletBalance = walletBalanceValue >= totalPrice;

    return (
      <div className="flex flex-column gap-3">
        <div className="font-medium text-sm" style={{ color: "#0A0D13" }}>
          {localeText("choosePaymentMethod")}
        </div>

        {/* Extracredit Option */}
        <div
          className="flex align-items-center gap-3 p-3 border-round border-1"
          style={{
            borderColor: selectedPaymentMethod === "extracredit" ? "#46A748" : "#d8d8d8",
            backgroundColor: selectedPaymentMethod === "extracredit" ? "#f0f9f0" : "white",
          }}
        >
          <RadioButton
            inputId="extracredit-option"
            name="payment-method"
            value="extracredit"
            checked={selectedPaymentMethod === "extracredit"}
            onChange={(e) => handlePaymentMethodChange(e.value)}
            disabled={!hasExtracredit || !hasEnoughExtracredit}
          />
          <label htmlFor="extracredit-option" className="flex-1 cursor-pointer">
            <div className="flex justify-content-between align-items-center">
              <div>
                <div className="font-medium">{localeText("paymentWithExtracredit")}</div>
                <small className="text-color-secondary">
                  {hasExtracredit
                    ? hasEnoughExtracredit
                      ? localeText("sufficientBalance")
                      : localeText("insufficientBalance")
                    : localeText("noBalanceAvailable")}
                </small>
              </div>
              <div className="text-right">
                <div className="font-semibold">
                  {currencyFormat[currencyProduct]}{" "}
                  {formatPrice(walletExtracoinBalanceForex || walletExtracoinBalance)}
                </div>
                <small className="text-color-secondary">
                  ({currencyFormat[currencyProduct]} {formatPrice(extracoinInWallet)}{" "}
                  {localeText("toBeUsed")})
                </small>
              </div>
            </div>
          </label>
        </div>

        {/* Wallet Option */}
        <div
          className="flex align-items-center gap-3 p-3 border-round border-1"
          style={{
            borderColor: selectedPaymentMethod === "wallet" ? "#46A748" : "#d8d8d8",
            backgroundColor: selectedPaymentMethod === "wallet" ? "#f0f9f0" : "white",
          }}
        >
          <RadioButton
            inputId="wallet-option"
            name="payment-method"
            value="wallet"
            checked={selectedPaymentMethod === "wallet"}
            onChange={(e) => handlePaymentMethodChange(e.value)}
            disabled={!hasWalletBalance || !hasEnoughWalletBalance}
          />
          <label htmlFor="wallet-option" className="flex-1 cursor-pointer">
            <div className="flex justify-content-between align-items-center">
              <div>
                <div className="font-medium">{localeText("paymentWithWallet")}</div>
                <small className="text-color-secondary">
                  {hasWalletBalance
                    ? hasEnoughWalletBalance
                      ? localeText("sufficientBalance")
                      : localeText("insufficientBalance")
                    : localeText("noBalanceAvailable")}
                </small>
              </div>
              <div className="text-right">
                <div className="font-semibold">
                  {currencyFormat[currencyProduct]} {formatPrice(walletBalanceValue)}
                </div>
                <small className="text-color-secondary">{localeText("balance")}</small>
              </div>
            </div>
          </label>
        </div>

        {/* Total to Pay */}
        <div className="p-3 bg-gray-50 border-round">
          <div className="flex justify-content-between align-items-center">
            <span className="font-medium">{localeText("total")}:</span>
            <span className="font-semibold text-lg">
              {currencyFormat[currencyProduct]} {formatPrice(totalPrice)}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-column gap-4">
      {/* Extracredit Section */}
      <div className="flex flex-column gap-2">
        <div className="font-medium text-xs" style={{ color: "#0A0D13" }}>
          {localeText("paymentWithExtracredit")} {currencyFormat[currencyProduct]}{" "}
          {formatPrice(walletExtracoinBalanceForex || walletExtracoinBalance)} (
          {currencyFormat[currencyProduct]} {formatPrice(extracoinInWallet)}{" "}
          {localeText("toBeUsed")})
        </div>
        <SinglePaymentExtracredit
          currencyProduct={currencyProduct}
          currencyFormat={currencyFormat}
          extracoinInWallet={extracoinInWallet}
          combinePayment={combinePayment}
          totalPrice={totalPrice}
          isInCombinedAccordion={true}
        />
      </div>

      {/* Wallet Balance Section */}
      <div className="flex flex-column gap-2">
        <div className="font-medium text-xs" style={{ color: "#0A0D13" }}>
          {localeText("paymentWithWallet")} {currencyFormat[currencyProduct]}{" "}
          {formatPrice(walletBalanceValue)}
        </div>
        <SinglePaymentWallet
          currencyProduct={currencyProduct}
          currencyFormat={currencyFormat}
          walletBalanceValue={walletBalanceValue}
          combinePayment={combinePayment}
          totalPrice={totalPrice}
          isInCombinedAccordion={true}
        />
      </div>

      {/* Info about combining payments */}
      <div className="p-3 bg-blue-50 border-round border-blue-200">
        <div className="flex align-items-center gap-2">
          <i className="pi pi-info-circle text-blue-600"></i>
          <span className="text-blue-800 font-medium">{localeText("paymentCombination")}</span>
        </div>
        <small className="text-blue-700 mt-1 block">
          {localeText("paymentCombinationExplanation")}
        </small>
      </div>
    </div>
  );
};

export default SinglePaymentCreditCombined;
